import React, { FC } from 'react'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { MapPinIcon } from '@/atoms/Icons/MapPinIcon'
import { Text, TitleMD } from '@/atoms/Text'
import { VenueSelectionList } from '@/organisms/TheaterPreferences/components/VenueSelectionList'
import { useTranslate } from '@/utils/translate/translate-client'
import { AddressSearch } from '@/views/TicketCheckoutViews/ShowtimesView/components/AddressSearch'
import { useTheaterPreferences } from '../TheaterPreferencesContext'

interface TheaterSelectionStepProps {
  effectiveCountryCode: string
  onNextStep: () => void
  onClose: () => void
}

export const TheaterSelectionStep: FC<TheaterSelectionStepProps> = ({ effectiveCountryCode, onNextStep, onClose }) => {
  const { t } = useTranslate('account')
  const {
    hasExistingPreferences,
    preferencesLoading,
    selectedVenueId,
    defaultAddressString,
    coordinates,
    loading,
    venues,
    venuesLoading,
    venuesError,
    location,
    handleAddressChange,
    handleVenueSelect,
  } = useTheaterPreferences()

  // Render the header for theater selection step
  const renderHeader = () => (
    <div className="mb-4 flex shrink-0 flex-col gap-1">
      <div className="flex items-center gap-2">
        <MapPinIcon className="h-4 w-4 text-gray-700" />
        <TitleMD weight="bold" className="text-base">
          {t('theaterPreferences', 'Theater Preferences')}
        </TitleMD>
      </div>
      <Text weight="normal" color="gray-600" className="text-sm leading-6 text-gray-600">
        {hasExistingPreferences
          ? t(
              'editTheaterPreferencesDescription',
              'Edit your preferred location to update your personalized movie recommendations and showtimes.',
            )
          : t(
              'theaterPreferencesDescription',
              'Set your preferred location to get personalized movie recommendations and showtimes.',
            )}
      </Text>
    </div>
  )

  // Render theater selection form
  const renderTheaterSelectionForm = () => (
    <div className="flex h-full min-h-0 flex-col">
      <FlexColumn className="mb-4 shrink-0 gap-1">
        <Text color="gray-600" className="text-xs">
          {hasExistingPreferences
            ? t('updateLocation', 'Update Preferred Location')
            : t('preferredLocation', 'Preferred Location')}
        </Text>
        <div className="relative z-50 h-10">
          <AddressSearch
            countryCode={effectiveCountryCode}
            onChange={handleAddressChange}
            defaultAddressString={location ? location : defaultAddressString}
            className="w-full"
            scrollToVenues={() => {}}
            scrollToContentTop={() => {}}
          />
        </div>
      </FlexColumn>

      <div className="h-full min-h-0 grow overflow-hidden">
        <FlexColumn className="relative z-10 h-full min-h-0">{renderVenueSelection()}</FlexColumn>
      </div>
      <div className="shrink-0">{renderActionButtons()}</div>
    </div>
  )

  // Render venue selection
  const renderVenueSelection = () => {
    if (venues.length > 0) {
      return (
        <VenueSelectionList
          venues={venues}
          loading={venuesLoading}
          error={!!venuesError}
          selectedVenueId={selectedVenueId}
          onVenueSelect={handleVenueSelect}
        />
      )
    }

    return (
      <div className="flex items-center justify-center py-8">
        <Text className="text-sm text-gray-500">
          {hasExistingPreferences
            ? t('updateSearchPrompt', 'Enter a new location above to update your theater.')
            : t('searchPrompt', 'Enter a location above to find theaters near you.')}
        </Text>
      </div>
    )
  }

  // Render action buttons
  const renderActionButtons = () => (
    <div className="fixed inset-x-0 bottom-0 z-50 w-full shrink-0 border-t border-gray-200 bg-white p-6">
      <div className="flex w-full justify-between gap-2">
        <Button variant="lightgray" onClick={() => onClose()} className="flex-1 py-3 text-sm">
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          variant="black"
          onClick={onNextStep}
          className="flex-1 py-3 text-sm"
          disabled={(coordinates !== null && !selectedVenueId) || loading}
        >
          {t('next', 'Next')}
        </Button>
      </div>
    </div>
  )

  // Adjust the main layout to account for the fixed footer
  return (
    <div className="flex h-full flex-col p-6 pb-[72px] md:p-10">
      {renderHeader()}

      {preferencesLoading ? (
        <div className="flex grow items-center justify-center">
          <Text className="text-sm text-gray-500">{t('loadingYourPreferences', 'Loading your preferences...')}</Text>
        </div>
      ) : (
        <div className="flex h-full min-h-0 grow flex-col">{renderTheaterSelectionForm()}</div>
      )}
    </div>
  )
}
